import {X, Trash2, Edit2} from 'lucide-react';
import {useNavigate} from 'react-router-dom';
import {useShopping} from '../contexts/ShoppingContext';
import {useGuestMode} from '../hooks/useGuestMode';
import {QuantitySelector, Modal} from './ui';
import {CalendarCustomizer} from './CalendarCustomizer';
import {BusinessCardCustomizer} from './BusinessCardCustomizer';
import {CalendarCustomization, BusinessCardCustomization, CartItem} from '../types';
import {useState} from 'react';

const authCheckoutEnabled = import.meta.env.VITE_AUTH_CHECKOUT_ENABLED === 'true';

export function Cart() {
    const navigate = useNavigate();
    const {state, dispatch} = useShopping();
    const {isGuest} = useGuestMode();
    const {cart, isCartOpen} = state;
    const [editingCartItem, setEditingCartItem] = useState<CartItem | null>(null);
    const [editingCustomization, setEditingCustomization] = useState<CalendarCustomization | null>(null);
    const [editingBusinessCardCustomization, setEditingBusinessCardCustomization] = useState<BusinessCardCustomization | null>(null);

    const totalPrice = cart.reduce((total, item) => {
        const personalizationFee = item.personalizations?.reduce((sum, p) => sum + (p.fee ?? 0), 0) ?? 0;
        return total + (item.variation.price + personalizationFee) * item.quantity;
    }, 0);

    const handleUpdateQuantity = (cartItemId: string, newQuantity: number) => {
        dispatch({
            type: 'UPDATE_QUANTITY',
            payload: {cartItemId, quantity: newQuantity}
        });
    };

    const handleRemoveItem = (cartItemId: string) => {
        dispatch({
            type: 'REMOVE_FROM_CART',
            payload: cartItemId
        });
    };

    const handleClose = () => {
        dispatch({type: 'SET_CART_OPEN', payload: false});
    };

    const handleEditCustomization = (item: CartItem) => {
        setEditingCartItem(item);
        setEditingCustomization(item.calendarCustomization || null);
    };

    const handleEditBusinessCardCustomization = (item: CartItem) => {
        setEditingCartItem(item);
        setEditingBusinessCardCustomization(item.businessCardCustomization || null);
    };

    const handleSaveCustomization = () => {
        if (editingCartItem && editingCustomization) {
            const cartItemId = editingCartItem.id || `${editingCartItem.productId}-${editingCartItem.variationId}`;
            dispatch({
                type: 'UPDATE_CALENDAR_CUSTOMIZATION',
                payload: {cartItemId, calendarCustomization: editingCustomization}
            });
            setEditingCartItem(null);
            setEditingCustomization(null);
        }
    };

    const handleSaveBusinessCardCustomization = () => {
        if (editingCartItem && editingBusinessCardCustomization) {
            const cartItemId = editingCartItem.id || `${editingCartItem.productId}-${editingCartItem.variationId}`;
            dispatch({
                type: 'UPDATE_BUSINESS_CARD_CUSTOMIZATION',
                payload: {cartItemId, businessCardCustomization: editingBusinessCardCustomization}
            });
            setEditingCartItem(null);
            setEditingBusinessCardCustomization(null);
        }
    };

    const handleCancelEdit = () => {
        setEditingCartItem(null);
        setEditingCustomization(null);
        setEditingBusinessCardCustomization(null);
    };

    // Mobile: Full-screen overlay - safely check window in browser environment
    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

    const handleClearCart = () => dispatch({type: 'CLEAR_CART'})

    const cartContent = (
        <>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <button
                    onClick={() => {
                        handleClose();
                        navigate('/cart');
                    }}
                    className="text-xl font-semibold text-gray-900 hover:text-blue-600 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
                    id="cart-title"
                    aria-label="View full shopping cart"
                >
                    Shopping Cart
                </button>

                <div className="flex items-center justify-between gap-4">
                    {cart.length > 0 && (
                        <button
                            onClick={handleClearCart}
                            className="text-sm text-red-600 hover:text-red-700 underline"
                        >
                            Clear all
                        </button>
                    )}

                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        aria-label="Close shopping cart"
                    >
                        <X className="w-5 h-5" aria-hidden="true"/>
                    </button>
                </div>
            </div>

            {!isGuest && !authCheckoutEnabled && (
                <div className="mx-6 mt-4 mb-2 flex flex-row p-4 bg-[#f0f7ff] border border-[#cfe4ff] rounded-lg font-sans">
                    <div className="mr-3 text-lg pt-0.5">
                        ✨
                    </div>
                    <div className="flex flex-col gap-2">
                        <h3 className="text-[#1a4a8e] text-base font-semibold leading-tight">
                            Checkout is coming soon!
                        </h3>
                        <p className="text-[#4a6fa5] text-sm leading-relaxed">
                            You're previewing our new checkout experience. Please use the current checkout to finish your order.
                        </p>
                        <button
                            onClick={() => {
                                handleClose();
                                window.location.href = '/cart';
                            }}
                            className="bg-[#1d5ed2] hover:bg-[#164eb3] text-white font-semibold py-2 px-4 rounded-md text-sm w-fit transition-colors duration-200"
                        >
                            Complete Your Order &rarr;
                        </button>
                    </div>
                </div>
            )}

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-6" role="region" aria-live="polite">
                {cart.length === 0 ? (
                    <div className="text-center py-12" role="status">
                        <div
                            className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center"
                            aria-hidden="true">
                            <X className="w-8 h-8 text-gray-400"/>
                        </div>
                        <p className="text-gray-500 text-lg">Your cart is empty</p>
                        <p className="text-gray-800 text-sm mt-1">Add some items to get started</p>
                    </div>
                ) : (
                    <div className="space-y-6" role="list" aria-label="Cart items">
                        {Object.entries(
                            cart.reduce<Record<string, CartItem[]>>((groups, item) => {
                                const key = item.product.productionTimeline ?? 'Ships Soon';
                                (groups[key] ??= []).push(item);
                                return groups;
                            }, {})
                        ).map(([timelineName, items]) => (
                            <div key={timelineName}>
                                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                                    </svg>
                                    <span className="text-xs font-semibold text-gray-600">Shipping Timeline:</span>
                                    <span className="text-xs text-gray-900">{timelineName}</span>
                                </div>
                                <div className="space-y-6">
                                {items.map((item) => {
                                    const cartItemId = item.id || `${item.productId}-${item.variationId}`;
                                    const firstImage = item.variation.images && item.variation.images.length > 0
                                        ? item.variation.images[0]
                                        : item.product.images[0];
                                    const variationImage = typeof firstImage === 'string' ? firstImage : (firstImage ? firstImage.url : "");
                                    return (
                                        <div key={cartItemId} className="flex space-x-4" role="listitem">
                                            {/* Product Image */}
                                            <div className="flex-shrink-0">
                                                <img
                                                    src={variationImage}
                                                    alt={`${item.product.name} - ${item.variation.color}${item.variation.size !== 'Standard' ? `, ${item.variation.size}` : ''}`}
                                                    className="w-16 h-16 rounded-lg object-cover"
                                                />
                                            </div>

                                            {/* Product Details */}
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-sm font-medium text-gray-900 truncate">
                                                    {item.product.name}
                                                </h3>
                                                <div className="mt-1 text-sm text-gray-500">
                                                    {item.variation.size !== 'Standard' && (
                                                        <span>Size: {item.variation.size} • </span>
                                                    )}
                                                    Color: {item.variation.color}
                                                    {item.logoOptionName && (
                                                        <span> • Logo: {item.logoOptionName}</span>
                                                    )}
                                                </div>
                                                {item.personalizations?.map((p, i) => (
                                                    <div key={i} className="mt-2 p-2 bg-blue-100 rounded-md border border-blue-200">
                                                        <p className="text-xs font-medium text-blue-900">
                                                            {item.personalizations!.length > 1 ? `Personalization #${i + 1}` : 'Personalization'}
                                                        </p>
                                                        <p className="text-xs text-blue-700">{p.text}</p>
                                                        {p.placement && (
                                                            <p className="text-xs text-blue-600">Placement: {p.placement}</p>
                                                        )}
                                                        {p.fee != null && p.fee > 0 && (
                                                            <p className="text-xs text-blue-600">+${p.fee.toFixed(2)} per unit</p>
                                                        )}
                                                    </div>
                                                ))}
                                                {item.calendarCustomization && (
                                                    <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-100">
                                                        <div className="flex items-start justify-between mb-1">
                                                            <p className="text-xs font-medium text-blue-900">Calendar Customization</p>
                                                            <button
                                                                onClick={() => handleEditCustomization(item)}
                                                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                                                aria-label="Edit calendar customization"
                                                            >
                                                                <Edit2 className="w-3 h-3"/>
                                                            </button>
                                                        </div>
                                                        <div className="text-xs text-blue-700 space-y-0.5">
                                                            {item.calendarCustomization.locations.primary.street && (
                                                                <p>{item.calendarCustomization.locations.primary.street}, {item.calendarCustomization.locations.primary.city}</p>
                                                            )}
                                                            {item.calendarCustomization.locations.secondary?.street && (
                                                                <p>{item.calendarCustomization.locations.secondary.street}, {item.calendarCustomization.locations.secondary.city}</p>
                                                            )}
                                                            {item.calendarCustomization.locations.tertiary?.street && (
                                                                <p>{item.calendarCustomization.locations.tertiary.street}, {item.calendarCustomization.locations.tertiary.city}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                                {item.businessCardCustomization && (
                                                    <div className="mt-2 p-2 bg-green-50 rounded-md border border-green-100">
                                                        <div className="flex items-start justify-between">
                                                            <p className="text-xs font-medium text-green-900">Business Card Customization</p>
                                                            <button
                                                                onClick={() => handleEditBusinessCardCustomization(item)}
                                                                className="text-green-600 hover:text-green-800 transition-colors"
                                                                aria-label="Edit business card customization"
                                                            >
                                                                <Edit2 className="w-3 h-3"/>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {(() => {
                                                    const pFee = item.personalizations?.reduce((sum, p) => sum + (p.fee ?? 0), 0) ?? 0;
                                                    const unitPrice = Number(item.variation.price) + pFee;
                                                    return (
                                                        <div className="mt-1 flex items-center justify-between">
                                                            <div className="flex flex-col">
                                                                <span className="text-xs text-gray-500">Unit Price</span>
                                                                <span className="text-sm font-medium text-gray-900">
                                                                    ${unitPrice.toFixed(2)}
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-col items-end">
                                                                <span className="text-xs text-gray-500">Subtotal</span>
                                                                <span className="text-sm font-semibold text-gray-900">
                                                                    ${(unitPrice * item.quantity).toFixed(2)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                {/* Quantity Controls */}
                                                <div className="mt-2 flex items-center justify-between">
                                                    <QuantitySelector
                                                        quantity={item.quantity}
                                                        onQuantityChange={(newQuantity) => handleUpdateQuantity(cartItemId, newQuantity)}
                                                        size="sm"
                                                        stock={item.variation.stock}
                                                        allowOnDemand={item.product.allowOnDemand}
                                                    />
                                                    <button
                                                        onClick={() => handleRemoveItem(cartItemId)}
                                                        className="p-1 hover:bg-red-100 hover:text-red-600 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                                        aria-label={`Remove ${item.product.name} from cart`}
                                                    >
                                                        <Trash2 className="w-4 h-4" aria-hidden="true"/>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer */}
            {cart.length > 0 && (
                <div className="border-t border-gray-200 p-6" role="region" aria-label="Cart summary">
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-lg font-medium text-gray-900">Total</span>
                        <span className="text-xl font-bold text-gray-900">${totalPrice.toFixed(2)}</span>
                    </div>
                    <button
                        onClick={() => {
                            handleClose();
                            navigate('/checkout');
                        }}
                        disabled={!isGuest && !authCheckoutEnabled}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
                        aria-label={`Proceed to checkout with total $${totalPrice.toFixed(2)}`}
                    >
                        Checkout
                    </button>
                </div>
            )}
        </>
    );

    // Mobile: Full screen
    if (isMobile) {
        return (
            <>
                <div
                    className={`fixed inset-0 z-50 bg-white transition-transform duration-300 ease-in-out ${
                        isCartOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="cart-title"
                >
                    <div className="h-full flex flex-col">
                        {cartContent}
                    </div>
                </div>

                {/* Edit Calendar Customization Modal */}
                {editingCartItem && editingCustomization && (
                    <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Calendar Customization">
                        <div className="max-h-[70vh] overflow-y-auto">
                            <CalendarCustomizer
                                onCustomizationChange={setEditingCustomization}
                                initialCustomization={editingCustomization}
                                variation={editingCartItem.variation}
                                productImages={editingCartItem.product.images}
                            />
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveCustomization}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Save Changes
                            </button>
                        </div>
                    </Modal>
                )}

                {/* Edit Business Card Customization Modal */}
                {editingCartItem && editingBusinessCardCustomization && (
                    <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Business Card Customization" size={"lg"}>
                        <BusinessCardCustomizer
                            onCustomizationChange={setEditingBusinessCardCustomization}
                            withBorder={false}
                            initialCustomization={editingBusinessCardCustomization}
                            productImages={editingCartItem.product.images}
                            previewImage={editingCartItem.product.printImages?.frontForReview}
                            style={editingCartItem.product.printOnDemandStyle}
                            fields={editingCartItem.product.printOnDemandFields}
                            options={editingCartItem.product.printOnDemandOptions}
                        />
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveBusinessCardCustomization}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-300 rounded-md cursor-not-allowed"
                                disabled
                            >
                                Save Changes
                            </button>
                        </div>
                    </Modal>
                )}
            </>
        );
    }

    // Desktop: Side panel
    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 z-50 bg-black/25 backdrop-blur-sm transition-opacity duration-300 ${
                    isCartOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                }`}
                onClick={handleClose}
                aria-hidden="true"
            />

            {/* Side Panel */}
            <div
                className={`fixed z-[100] right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
                    isCartOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
                onMouseEnter={() => dispatch({type: 'SET_CART_OPEN', payload: true})}
                role="dialog"
                aria-modal="true"
                aria-labelledby="cart-title"
            >
                <div className="h-full flex flex-col">
                    {cartContent}
                </div>
            </div>

            {/* Edit Calendar Customization Modal */}
            {editingCartItem && editingCustomization && (
                <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Calendar Customization">
                    <div className="max-h-[70vh] overflow-y-auto">
                        <CalendarCustomizer
                            onCustomizationChange={setEditingCustomization}
                            initialCustomization={editingCustomization}
                            variation={editingCartItem.variation}
                            productImages={editingCartItem.product.images}
                        />
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            onClick={handleCancelEdit}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveCustomization}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Save Changes
                        </button>
                    </div>
                </Modal>
            )}

            {/* Edit Business Card Customization Modal */}
            {editingCartItem && editingBusinessCardCustomization && (
                <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Business Card Customization" size={"lg"}>
                     <BusinessCardCustomizer
                            onCustomizationChange={setEditingBusinessCardCustomization}
                            withBorder={false}
                            initialCustomization={editingBusinessCardCustomization}
                            productImages={editingCartItem.product.images}
                            previewImage={editingCartItem.product.printImages?.frontForReview}
                            style={editingCartItem.product.printOnDemandStyle}
                            fields={editingCartItem.product.printOnDemandFields}
                            options={editingCartItem.product.printOnDemandOptions}
                        />
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            onClick={handleCancelEdit}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveBusinessCardCustomization}
                            disabled
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-300 rounded-md cursor-not-allowed"
                        >
                            Save Changes
                        </button>
                    </div>
                </Modal>
            )}
        </>
    );
}
